import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, XMarkIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { useListingRequests } from '../../hooks/useListingRequests'
import LoadingSpinner from '../LoadingSpinner'
import { toast } from '../../services/toastService'

const STATUS_PILL = {
  pending:  'bg-amber-100 text-amber-700',
  accepted: 'bg-primary/10 text-primary',
  declined: 'bg-gray-100 text-gray-500',
}

/**
 * ProfileListingRequests - inbox of incoming requests for the poster's listings.
 * Shows requester name, community standing (sighting count), and their message.
 * Poster can accept (opens channel) or decline each request.
 *
 * @param {Function} onTabChange - navigate to Contact tab with channel_id on accept
 * @idempotent @atomic
 */
const ProfileListingRequests = memo(function ProfileListingRequests({ onTabChange, enabled = true }) {
  const { t } = useTranslation()
  const { requests, loading, error, respondToRequest } = useListingRequests(enabled)

  const handleAccept = useCallback(async (req) => {
    await respondToRequest(req.id, 'accepted', {
      onSuccess: (data) => {
        toast.success(t('listings.request.accepted', 'Request accepted — channel opened'))
        if (data.channel_id && onTabChange) {
          onTabChange('contact', null, data.channel_id)
        }
      },
      onError: () => toast.error(t('listings.request.updateFailed', 'Failed to respond')),
    })
  }, [respondToRequest, t, onTabChange])

  const handleDecline = useCallback(async (req) => {
    await respondToRequest(req.id, 'declined', {
      onSuccess: () => toast.success(t('listings.request.declined', 'Request declined')),
      onError: () => toast.error(t('listings.request.updateFailed', 'Failed to respond')),
    })
  }, [respondToRequest, t])

  if (loading) return <LoadingSpinner size="sm" />
  if (error) return <p className="text-sm text-red-500 py-2 text-center">{t('common.error', 'Failed to load')}</p>
  if (!requests.length) return (
    <p className="text-xs text-gray-500 py-3 text-center">{t('listings.request.empty', 'No incoming requests')}</p>
  )

  return (
    <div className="space-y-2 p-1">
      {requests.map((req) => (
        <div key={req.id} className="bg-white rounded-lg border border-gray-200/60 p-3 space-y-2">
          {/* Requester header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{req.display_name}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {req.sighting_count} {t('listings.request.sightings', 'sightings')}
                {' · '}
                {t('listings.request.re', 'Re:')} <span className="font-medium text-gray-600 truncate">{req.listing_title}</span>
              </p>
            </div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_PILL[req.status] || STATUS_PILL.pending}`}>
              {t(`listings.request.status.${req.status}`, req.status)}
            </span>
          </div>

          {/* Message */}
          <p className="text-xs text-gray-600 bg-gray-50 rounded p-2 italic leading-snug">
            &ldquo;{req.message}&rdquo;
          </p>

          {/* Actions — only for pending */}
          {req.status === 'pending' && (
            <div className="flex gap-2">
              <button
                onClick={() => handleAccept(req)}
                onTouchEnd={(e) => { e.preventDefault(); handleAccept(req) }}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors"
                style={{ background: 'rgb(var(--color-primary))' }}
              >
                <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                {t('listings.request.acceptButton', 'Accept & chat')}
              </button>
              <button
                onClick={() => handleDecline(req)}
                onTouchEnd={(e) => { e.preventDefault(); handleDecline(req) }}
                className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5 flex-shrink-0" />
                {t('listings.request.declineButton', 'Decline')}
              </button>
            </div>
          )}

          {/* Already accepted — show go to chat */}
          {req.status === 'accepted' && req.channel_id && (
            <button
              onClick={() => onTabChange?.('contact', null, req.channel_id)}
              onTouchEnd={(e) => { e.preventDefault(); onTabChange?.('contact', null, req.channel_id) }}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium text-primary hover:bg-primary/5 transition-colors border border-primary/20"
            >
              <ChatBubbleLeftRightIcon className="w-3.5 h-3.5 flex-shrink-0" />
              {t('listings.request.openChat', 'Open chat')}
            </button>
          )}
        </div>
      ))}
    </div>
  )
})

export default ProfileListingRequests
